import React, { useEffect, useState } from 'react';
import DatePickerV2 from './DatePickerV2';
import './GoalCardV2.css';

function normalizeDate(dateValue) {
    if (!dateValue) return null;
    if (
        dateValue === '' ||
        dateValue === 'null' ||
        dateValue === 'undefined'
    ) {
        return null;
    }
    if (dateValue instanceof Date) {
        return !Number.isNaN(dateValue.getTime()) ? dateValue : null;
    }
    const parsed = new Date(dateValue);
    return !Number.isNaN(parsed.getTime()) ? parsed : null;
}

/**
 * Portal V2 goal card — same SelectedGoals wiring as classic SelectedGoal.
 */
export default function GoalCardV2({
    GoalIndex,
    AllState,
    areaName = 'Fitness',
    accent = '#15c285',
    placeholder,
}) {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth <= 767 : false
    );

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = () => setIsMobile(mq.matches);
        onChange();
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    const currGoal = AllState.SelectedGoals.find(
        (x) =>
            Number(x.areaId) === Number(AllState.CurrentF) &&
            Number(x.recomendationIndex) === Number(GoalIndex)
    );

    const value = (() => {
        if (currGoal == null) return { text: '', date: null };
        return {
            ...currGoal,
            date: normalizeDate(currGoal.date),
        };
    })();

    const isValid =
        String(value.text || '').trim().length > 0 && value.date != null;

    const GoalTextChange = (raw) => {
        const next = { ...AllState };
        const goalIndex = next.SelectedGoals.findIndex(
            (x) =>
                Number(x.areaId) === Number(AllState.CurrentF) &&
                Number(x.recomendationIndex) === Number(GoalIndex)
        );
        const text = String(raw)
            .replace(/^\s+/, '')
            .replace(/ {2}/g, ' ');
        if (next.SelectedGoals[goalIndex] != null) {
            next.SelectedGoals[goalIndex].text = text;
            AllState.changeData(next);
        } else {
            next.SelectedGoals.push({
                recomendationIndex: GoalIndex,
                text,
                areaId: AllState.CurrentF,
                date: new Date(),
                Id: next.SelectedGoals.length,
            });
            AllState.changeData(next);
        }
    };

    const setStartDate = (date) => {
        const next = { ...AllState };
        const goalIndex = next.SelectedGoals.findIndex(
            (x) =>
                Number(x.areaId) === Number(AllState.CurrentF) &&
                Number(x.recomendationIndex) === Number(GoalIndex)
        );
        if (next.SelectedGoals[goalIndex] != null) {
            next.SelectedGoals[goalIndex].date = date;
            AllState.changeData(next);
        }
    };

    const DateTrigger = React.forwardRef(({ value: dateLabel, onClick }, ref) => (
        <button
            type="button"
            className={`pv2-goal-card__date-btn${
                dateLabel ? '' : ' is-empty'
            }`}
            onClick={onClick}
            ref={ref}
            disabled={String(value.text || '').trim().length === 0}
            aria-label={`${areaName} goal ${GoalIndex + 1} target date`}
        >
            {dateLabel || 'Date'}
        </button>
    ));

    const num = String(GoalIndex + 1).padStart(2, '0');
    const inputId = `pv2-goal-${AllState.CurrentF}-${GoalIndex}`;
    const fieldProps = {
        id: inputId,
        className: 'pv2-goal-card__input',
        placeholder: placeholder || `Write a clear ${areaName} goal`,
        value: value.text || '',
        onChange: (e) => GoalTextChange(e.target.value),
        autoComplete: 'off',
    };

    return (
        <article
            className={`pv2-goal-card${isValid ? ' is-valid' : ''}`}
            style={{ ['--pv2-goal-accent']: accent }}
        >
            <span className="pv2-goal-card__num" aria-hidden="true">
                {num}
            </span>

            <div className="pv2-goal-card__body">
                <div className="pv2-goal-card__field">
                    <label className="pv2-goal-card__sr" htmlFor={inputId}>
                        {areaName
                            ? `${areaName} goal ${GoalIndex + 1}`
                            : `Goal ${GoalIndex + 1}`}
                    </label>
                    {isMobile ? (
                        <textarea
                            {...fieldProps}
                            rows={2}
                            className="pv2-goal-card__input pv2-goal-card__input--multi"
                        />
                    ) : (
                        <input {...fieldProps} type="text" />
                    )}
                </div>

                <div className="pv2-goal-card__field pv2-goal-card__field--date">
                    <DatePickerV2
                        selected={value.date}
                        onChange={(date) => setStartDate(date)}
                        minDate={new Date()}
                        disabled={String(value.text || '').trim().length === 0}
                        customInput={<DateTrigger />}
                        accent={accent}
                        label="Target date"
                        dateFormat={isMobile ? 'MMM d' : 'MM/dd/yyyy'}
                    />
                </div>
            </div>

            {isValid ? (
                <span
                    className="pv2-goal-card__check"
                    title="Goal ready — text and date set"
                    aria-label="Goal ready — text and date set"
                >
                    ✓
                </span>
            ) : null}
        </article>
    );
}
